import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
} from '@mui/material';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { RedirectRequest } from '@azure/msal-browser';
import { loginRequest } from '../config/AuthConfig.base';
import { useUserFlow } from '../hooks/UseUserFlow.hook';
import { useNavigate } from 'react-router-dom';

// Type for B2C ID Token Claims
interface B2CIdTokenClaims {
  emails?: string[];
  email?: string;
  tfp?: string;
  acr?: string;
  extension_TenantId?: string;
  [key: string]: unknown;
}

export const SignupRedirect: React.FC = () => {
  const { accounts, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const {
    determineFlow,
    isLoading: isFlowLoading,
    error: flowError,
  } = useUserFlow();
  const [error, setError] = useState<string | null>(null);
  const [isInitiatingLogin, setIsInitiatingLogin] = useState(false);
  const [hasInitiatedLogin, setHasInitiatedLogin] = useState(false);

  // Automatically initiate login on component mount when authenticated and no errors
  useEffect(() => {
    const initiateLogin = async () => {
      if (
        isAuthenticated &&
        accounts.length > 0 &&
        !hasInitiatedLogin &&
        !isInitiatingLogin &&
        !flowError &&
        !error
      ) {
        setHasInitiatedLogin(true);
        await performUserFlowLogin();
      }
    };

    initiateLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    accounts,
    hasInitiatedLogin,
    isInitiatingLogin,
    flowError,
    error,
  ]);

  const performUserFlowLogin = async () => {
    setIsInitiatingLogin(true);
    setError(null);

    try {
      // Get email from current account
      const account = accounts[0];
      if (!account) {
        throw new Error('No account found from signup flow');
      }

      // Extract email - prioritize emails array, then email field, then username
      const email =
        (account.idTokenClaims as B2CIdTokenClaims)?.emails?.[0] ||
        (account.idTokenClaims as B2CIdTokenClaims)?.email ||
        account.username;

      if (!email) {
        throw new Error('Could not extract email from account');
      }

      // Determine the appropriate user flow based on email
      const authority = await determineFlow(email);

      // Initiate interactive login with the determined authority
      const redirectRequest: RedirectRequest = {
        ...loginRequest,
        authority: authority,
        prompt: 'login',
        scopes: loginRequest.scopes,
        loginHint: email,
      };

      await instance.loginRedirect(redirectRequest);
    } catch (err) {
      setError(`Failed to initiate login: ${err}`);
      setIsInitiatingLogin(false);
    }
  };

  // Display error message in development mode with additional details
  const displayError = error || flowError;

  const handleNavigateToSignIn = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 3,
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom align="center">
            Completing Sign Up
          </Typography>

          {/* Error Alert */}
          {displayError && (
            <>
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to complete sign up process. Please try again later.
                {process.env['NODE_ENV'] === 'development' && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {displayError}
                  </Typography>
                )}
              </Alert>
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                onClick={handleNavigateToSignIn}
              >
                Go to Sign In
              </Button>
            </>
          )}

          {/* Loading State */}
          {(isFlowLoading || isInitiatingLogin) && !displayError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Waiting for authentication state */}
          {!isAuthenticated && !displayError && !isFlowLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <Typography variant="body2">
                Initializing authentication...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
